import {
  dateFromKey,
  instantTimeOf,
  multiDayEndDateKey,
  WEEKDAYS_JA,
  type EventOccurrence,
} from "@couples/shared";
import { Download, Heart } from "lucide-react";
import { useRef, type TouchEvent as ReactTouchEvent } from "react";

import { errorMessageOf } from "../lib/auth";
import { contrastTextColor, mixHexColors, withAlpha } from "../lib/color";
import { swipeDirectionOf } from "../lib/media/swipe";
import { downloadDayIcs } from "../lib/schedule/ics";
import { useToast } from "../lib/toast";
import SheetShell from "./SheetShell";

const DAY_START = 0;
const DAY_END = 24;
const HOUR_H = 48; // px
const TOTAL_MIN = (DAY_END - DAY_START) * 60;
const TOTAL_H = (DAY_END - DAY_START) * HOUR_H;

function minutesFromStart(time: string): number {
  const [h, m] = time.split(":").map(Number);
  if (h === undefined || m === undefined) throw new Error(`時刻の形式が不正です: ${time}`);
  return (h - DAY_START) * 60 + m;
}

function topPx(time: string): number {
  return Math.max(0, Math.min(TOTAL_H, (minutesFromStart(time) / TOTAL_MIN) * TOTAL_H));
}

function heightPx(start: string, end: string): number {
  return Math.max(topPx(end) - topPx(start), 22);
}

function laneOf(assignedTo: string | null, selfId: string): "you" | "partner" | "both" {
  if (assignedTo === null) return "both";
  return assignedTo === selfId ? "you" : "partner";
}

interface Props {
  date: string;
  occurrences: EventOccurrence[];
  selfId: string;
  selfName: string;
  partnerName: string;
  selfColor: string;
  partnerColor: string;
  /** ICS書き出しの CATEGORIES 用(→ handleExportIcs) */
  categoryNameById: ReadonlyMap<string, string>;
  onClose: () => void;
  onAdd: () => void;
  onEditEvent: (occurrence: EventOccurrence) => void;
  /** 左右スワイプでの前日・翌日への移動(→ Schedule.tsx の openDate) */
  onNavigate: (direction: "previous" | "next") => void;
}

/**
 * 日別の予定シート。
 *
 * 2人でアプリを使う前提のため、あなた/パートナーを左右のレーンに分け、
 * 同じ時間軸に沿って並べることで並行して見比べられるようにしている
 * (→ docs/design/schedule-mock.jsx)。2人の予定は両レーンにまたがる帯として描画する。
 */
export default function DaySheet({
  date,
  occurrences,
  selfId,
  selfName,
  partnerName,
  selfColor,
  partnerColor,
  categoryNameById,
  onClose,
  onAdd,
  onEditEvent,
  onNavigate,
}: Props) {
  const toast = useToast();
  const dayDate = dateFromKey(date);
  const weekday = WEEKDAYS_JA[dayDate.getDay()];
  const bothColor = mixHexColors(selfColor, partnerColor);

  /** この日ぶんの予定だけを ICS にする(繰り返しもその日の回だけの単発として)。→ lib/schedule/ics.ts */
  function handleExportIcs() {
    if (occurrences.length === 0) {
      toast("この日の予定はありません。");
      return;
    }
    try {
      const count = downloadDayIcs(date, occurrences, categoryNameById);
      toast(`${String(count)}件を書き出しました。`);
    } catch (error) {
      toast(errorMessageOf(error), "error");
    }
  }

  const touchStart = useRef<{ x: number; y: number } | null>(null);

  function onTouchStart(e: ReactTouchEvent<HTMLDivElement>) {
    const touch = e.touches[0];
    if (touch === undefined) return;
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchEnd(e: ReactTouchEvent<HTMLDivElement>) {
    const start = touchStart.current;
    touchStart.current = null;
    const touch = e.changedTouches[0];
    if (start === null || touch === undefined) return;
    const direction = swipeDirectionOf(touch.clientX - start.x, touch.clientY - start.y);
    if (direction !== null) onNavigate(direction);
  }

  // 日を跨ぐ予定は、その日の時刻だけ抜き出しても時間軸上に正しく置けないため、
  // 終日予定と同じ帯として上にまとめて出す(→ packages/shared/src/schedule.ts の multiDayEndDateKey)。
  const allDay = occurrences.filter((o) => o.event.allDay || multiDayEndDateKey(o.event) !== null);
  const timed = occurrences.filter((o) => !o.event.allDay && multiDayEndDateKey(o.event) === null);

  return (
    <SheetShell
      title={`${String(dayDate.getMonth() + 1)}月${String(dayDate.getDate())}日(${weekday})`}
      actions={
        <button type="button" onClick={handleExportIcs} aria-label="この日の予定を書き出し(.ics)">
          <Download className="size-5 text-[var(--color-sub)]" aria-hidden />
        </button>
      }
      onClose={onClose}
      tone="bg"
    >
      <div
        className="flex min-h-0 flex-1 flex-col"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {allDay.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-4 pt-2.5">
            {allDay.map((occurrence) => {
              const lane = laneOf(occurrence.event.assignedTo, selfId);
              const laneColor =
                lane === "both" ? bothColor : lane === "you" ? selfColor : partnerColor;
              const endKey = multiDayEndDateKey(occurrence.event);
              const endDate = endKey !== null ? dateFromKey(endKey) : null;
              return (
                <button
                  key={occurrence.event.id}
                  type="button"
                  onClick={() => {
                    onEditEvent(occurrence);
                  }}
                  className="rounded-full border-l-[3px] px-2.5 py-1 text-[11px]"
                  style={{
                    borderColor: laneColor,
                    backgroundColor: withAlpha(laneColor, "1a"),
                    color: laneColor,
                  }}
                >
                  {occurrence.event.title}
                  {endDate !== null && (
                    <span className="ml-1 opacity-70">
                      〜{endDate.getMonth() + 1}/{endDate.getDate()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex gap-2 px-4 pt-2.5 pb-1.5">
          <div className="w-10 shrink-0" />
          <div className="flex flex-1 gap-2">
            <span className="flex flex-1 items-center gap-1.5 text-[11.5px] font-semibold">
              <span className="size-2 rounded-full" style={{ backgroundColor: selfColor }} />
              {selfName}
            </span>
            <span className="flex flex-1 items-center gap-1.5 text-[11.5px] font-semibold">
              <span className="size-2 rounded-full" style={{ backgroundColor: partnerColor }} />
              {partnerName}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-2.5">
          <div className="flex gap-2">
            <div className="relative w-10 shrink-0" style={{ height: TOTAL_H }}>
              {Array.from({ length: DAY_END - DAY_START + 1 }, (_, i) => DAY_START + i).map((h) => (
                <span
                  key={h}
                  className="absolute left-0 -translate-y-1.5 text-[9.5px] text-[var(--color-sub)]"
                  style={{ top: (h - DAY_START) * HOUR_H }}
                >
                  {h}:00
                </span>
              ))}
            </div>
            <div
              className="relative flex-1"
              style={{
                height: TOTAL_H,
                backgroundImage:
                  "repeating-linear-gradient(to bottom, var(--color-line) 0, var(--color-line) 1px, transparent 1px, transparent " +
                  String(HOUR_H) +
                  "px)",
              }}
            >
              <div className="absolute inset-y-0 left-1/2 w-px bg-[var(--color-line)]" />
              {timed.length === 0 && (
                <p className="absolute inset-x-0 top-6 text-center text-[12px] text-[var(--color-sub)]">
                  この日の予定はありません。
                </p>
              )}
              {timed.map((occurrence) => {
                const { event } = occurrence;
                if (event.endsAt === null) return null;
                const start = instantTimeOf(event.startsAt);
                const end = instantTimeOf(event.endsAt);
                const top = topPx(start);
                const height = heightPx(start, end);
                const lane = laneOf(event.assignedTo, selfId);
                const laneColor =
                  lane === "both" ? bothColor : lane === "you" ? selfColor : partnerColor;
                const laneLayoutClass =
                  lane === "both"
                    ? "left-0 w-full"
                    : lane === "you"
                      ? "left-0 w-[calc(50%-4px)]"
                      : "left-[calc(50%+4px)] w-[calc(50%-4px)]";
                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => {
                      onEditEvent(occurrence);
                    }}
                    className={`absolute overflow-hidden rounded-lg px-1.5 py-1 text-left ${laneLayoutClass}`}
                    style={{
                      top,
                      height,
                      backgroundColor: laneColor,
                      color: contrastTextColor(laneColor),
                    }}
                  >
                    <div className="truncate text-[10.5px] font-semibold">{event.title}</div>
                    <div className="text-[9px] opacity-85">
                      {start}〜{end}
                    </div>
                    {lane === "both" && (
                      <div className="flex items-center gap-0.5 text-[8.5px] opacity-90">
                        <Heart className="size-2" fill="currentColor" aria-hidden />
                        2人
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-[var(--color-line)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onAdd}
            className="w-full rounded-xl bg-[var(--color-teal)] py-2.5 text-sm font-semibold text-white"
          >
            この日に追加
          </button>
        </div>
      </div>
    </SheetShell>
  );
}
